
import React, { useState } from 'react';
import { Card, Form, Input, Button, Modal, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { User } from '../../types';

const { Password } = Input;

interface SecuritySectionProps {
  user: User;
}

const SecuritySection: React.FC<SecuritySectionProps> = ({ user }) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);

  const showModal = () => {
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handlePasswordChange = async () => {
    try {
      const values = await form.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        message.error("Passwords do not match.");
        return;
      }

      setLoading(true);

      // Replace with your API endpoint for changing password
      const response = await api.post(`/user/me/change-password`, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      if (response.status === 200) {
        message.success('Password changed successfully.');
        setIsModalVisible(false);
      } else {
        throw new Error('Failed to change password.');
      }
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || 'An error occurred while changing the password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title="Security Settings"
      extra={<Button icon={<LockOutlined />} onClick={showModal}>Change Password</Button>}
      style={{ marginBottom: '1rem' }}
    >
      <p>Password Strength: <strong>Strong</strong></p>
      <p>Last Password Change: {user.lastPasswordChange ? new Date(user.lastPasswordChange).toLocaleDateString() : 'Never'}</p>

      {/* Change Password Modal */}
      <Modal
        title="Change Password"
        visible={isModalVisible}
        onOk={handlePasswordChange}
        onCancel={handleCancel}
        confirmLoading={loading}
        okText="Change Password"
      >
        <Form
          form={form}
          layout="vertical"
          name="change_password_form"
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password.' }]}
          >
            <Password placeholder="Current Password" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password.' },
              { min: 8, message: 'Password must be at least 8 characters.' },
              // Add more rules like regex for complexity if needed
            ]}
            hasFeedback
          >
            <Password placeholder="New Password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            hasFeedback
            rules={[
              { required: true, message: 'Please confirm your new password.' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match.'));
                },
              }),
            ]}
          >
            <Password placeholder="Confirm New Password" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default SecuritySection;
